import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useStoreApi } from '../api';
import { storeKeys } from '../query-key-factory';
import { StoreItem, StoreItemResponse } from '../types';
import { AthonStoreItem } from '../types/athon';
import { CampaignStoreItem } from '../types/campaign';

// Create all possible fields for this
type CampaignBodyFields = Partial<
  Pick<
    CampaignStoreItem,
    'name' | 'goal' | 'show_goal' | 'starting_at' | 'expires_at' | 'show_amount_raised'
  >
>;
// Mark whatever fields that are not optional
interface CampaignBody extends CampaignBodyFields {
  name: string;
  participant_options: Partial<CampaignStoreItem['participant_options']>;
  team_options: Partial<CampaignStoreItem['team_options']>;
}

interface AthonsBody extends CampaignBodyFields {
  name: string;
  participant_options: Partial<CampaignStoreItem['participant_options']>;
  team_options: Partial<CampaignStoreItem['team_options']>;
}
// We need a dynamic expected body type here.
interface CreateCrowdFundingBody<T> {
  crowdFundingItem: T extends 'StoreItems::Campaign'
    ? CampaignBody
    : T extends 'StoreItems::Athon'
      ? AthonsBody
      : never;
}

const useCreateCampaign = <T extends StoreItem['type']>(campaignType: T) => {
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();

  const createCampaign = async ({
    crowdFundingItem,
    causeId,
  }: CreateCrowdFundingBody<T> & { causeId?: string }) => {
    const apiRoute = campaignType.toLocaleLowerCase().includes('athon') ? 'athons' : 'campaigns';
    const { data } = await StoreApi.post<StoreItemResponse<CampaignStoreItem | AthonStoreItem>>(
      `/store_items/${apiRoute}`,
      { cause_id: causeId, store_item: crowdFundingItem }
    );

    return data.store_item;
  };
  return useMutation({
    mutationFn: createCampaign,
    onSuccess(data, variables) {
      const { causeId } = variables;
      const queryKey = causeId ? storeKeys.id(causeId).items.campaigns.all : [];
      queryClient.invalidateQueries({ queryKey: queryKey });
    },
  });
};

const useDuplicateCampaign = () => {
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();

  async function duplicateCampaign({
    campaignId,
    causeId,
  }: {
    campaignId: string;
    causeId: string;
  }) {
    const { data } = await StoreApi.post<StoreItemResponse<CampaignStoreItem>>(
      `/store_items/${campaignId}/duplicate?cause_id=${causeId}`,
      {
        cause_id: causeId,
        uuid: campaignId,
      }
    );
    return data.store_item;
  }
  return useMutation({
    mutationFn: duplicateCampaign,
    onSuccess: (_data, variables) => {
      const { causeId } = variables;

      const queryKey = causeId ? storeKeys.id(causeId).items.campaigns.all : [];
      queryClient.invalidateQueries({ queryKey: queryKey });
      queryClient.invalidateQueries({
        queryKey: storeKeys.id(causeId).items.fundraisers.all,
      });
    },
  });
};

export { useCreateCampaign, useDuplicateCampaign };
